import { Component, EventEmitter, Input, Output } from "@angular/core";

/**
 * Term selector (design system). Renders a single-select row of {@link
 * DsTermChipComponent} chips (e.g. "24 meses", "36 meses", ...) so a user can
 * quickly switch the term used to preview an amount.
 *
 * It only manages the selected term and emits changes; it carries no business
 * logic and persists nothing. Two-way binding is supported via
 * `[(selectedTerm)]`. Provide `prices` to show a per-term price sub-label on
 * each chip.
 */
@Component({
  selector: "ds-term-selector",
  templateUrl: "./ds-term-selector.component.html",
  styleUrls: ["./ds-term-selector.component.scss"],
})
export class DsTermSelectorComponent {
  /** Terms (in months) rendered as chips. */
  @Input() terms: number[] = [24, 36, 48, 60];

  /** Currently selected term (in months). `null` renders no active chip. */
  @Input() selectedTerm: number | null = null;

  /** Unit label appended after each term value (e.g. "24 meses"). */
  @Input() suffix = "meses";

  /** Optional label shown above the chips. */
  @Input() label = "";

  /** Disables interaction with every chip. */
  @Input() disabled = false;

  /** Optional per-term formatted price. When provided, chips show a price sub-label. */
  @Input() prices: { [term: number]: string } | null = null;

  @Output() selectedTermChange = new EventEmitter<number>();

  get showPrice(): boolean {
    return !!this.prices;
  }

  chipLabel(term: number): string {
    return this.suffix ? `${term} ${this.suffix}` : `${term}`;
  }

  priceFor(term: number): string {
    return this.prices?.[term] ?? "";
  }

  selectTerm(term: number): void {
    if (this.disabled || term === this.selectedTerm) return;
    this.selectedTerm = term;
    this.selectedTermChange.emit(term);
  }

  trackByTerm(_index: number, term: number): number {
    return term;
  }
}
